/**
 * Simple toast notifications - Replace all alert() calls with this
 * No library needed, pure CSS/JS
 */

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastOptions {
  duration?: number
  position?: 'top-right' | 'top-center' | 'bottom-right' | 'bottom-center'
}

class ToastManager {
  private container: HTMLDivElement | null = null
  private toasts: Set<HTMLDivElement> = new Set()

  private getContainer() {
    if (!this.container && typeof document !== 'undefined') {
      // Create container
      this.container = document.createElement('div')
      this.container.id = 'toast-container'
      this.container.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 99999;
        pointer-events: none;
      `
      document.body.appendChild(this.container)
    }
    return this.container
  }

  private createToast(message: string, type: ToastType, options: ToastOptions = {}) {
    const container = this.getContainer()
    if (!container) return

    const toast = document.createElement('div')
    toast.style.cssText = `
      background: ${this.getBackgroundColor(type)};
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      margin-bottom: 10px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 250px;
      max-width: 400px;
      animation: slideIn 0.3s ease;
      pointer-events: auto;
      cursor: pointer;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 14px;
      line-height: 1.5;
      word-wrap: break-word;
    `

    // Add icon
    const icon = document.createElement('span')
    icon.textContent = this.getIcon(type)
    icon.style.fontSize = '18px'

    // Add message
    const text = document.createElement('span')
    text.textContent = message
    text.style.flex = '1'

    toast.appendChild(icon)
    toast.appendChild(text)

    // Click to dismiss
    toast.onclick = () => this.removeToast(toast)

    container.appendChild(toast)
    this.toasts.add(toast)

    // Auto remove after duration
    const duration = options.duration || 3000
    setTimeout(() => this.removeToast(toast), duration)

    // Add animation styles if not already added
    if (!document.getElementById('toast-styles')) {
      const style = document.createElement('style')
      style.id = 'toast-styles'
      style.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `
      document.head.appendChild(style)
    }
  }

  private removeToast(toast: HTMLDivElement) {
    if (!this.toasts.has(toast)) return

    toast.style.animation = 'slideOut 0.3s ease'
    setTimeout(() => {
      toast.remove()
      this.toasts.delete(toast)
    }, 300)
  }

  private getBackgroundColor(type: ToastType): string {
    switch (type) {
      case 'success':
        return '#10b981'
      case 'error':
        return '#fca5a5'
      case 'warning':
        return '#f59e0b'
      case 'info':
        return '#3b82f6'
      default:
        return '#6b7280'
    }
  }

  private getIcon(type: ToastType): string {
    switch (type) {
      case 'success':
        return '✅'
      case 'error':
        return '❌'
      case 'warning':
        return '⚠️'
      case 'info':
        return 'ℹ️'
      default:
        return '📢'
    }
  }

  // Public methods
  success(message: string, options?: ToastOptions) {
    this.createToast(message, 'success', options)
  }

  error(message: string, options?: ToastOptions) {
    this.createToast(message, 'error', options)

    // Also log to console and Sentry
    console.error('[Toast Error]', message)
    if ((window as any).Sentry) {
      ;(window as any).Sentry.captureMessage(message, 'error')
    }
  }

  warning(message: string, options?: ToastOptions) {
    this.createToast(message, 'warning', options)
  }

  info(message: string, options?: ToastOptions) {
    this.createToast(message, 'info', options)
  }

  // Clear all toasts
  clear() {
    this.toasts.forEach((toast) => this.removeToast(toast))
  }
}

// Create singleton instance
const toastManager = new ToastManager()

// Export simple API
export const toast = {
  success: (message: string, options?: ToastOptions) => toastManager.success(message, options),
  error: (message: string, options?: ToastOptions) => toastManager.error(message, options),
  warning: (message: string, options?: ToastOptions) => toastManager.warning(message, options),
  info: (message: string, options?: ToastOptions) => toastManager.info(message, options),
  clear: () => toastManager.clear(),
}

/**
 * HOW TO USE:
 *
 * import { toast, showError } from './utils/toast'
 *
 * // Replace alert('Error!') with:
 * toast.error('Something went wrong!')
 *
 * // Replace alert('Success!') with:
 * toast.success('Saved successfully!')
 *
 * // In try-catch blocks:
 * try {
 *   await saveData()
 *   toast.success('Saved!')
 * } catch (error) {
 *   toast.error(error)
 * }
 *
 * // Other types:
 * toast.info('Loading data...')
 * toast.warning('Your session will expire soon')
 *
 * // With options:
 * toast.error('Network error', {
 *   duration: 5000, // 5 seconds
 *   position: 'top-center'
 * })
 */
